/**
 * @file index.tsx
 *
 * @fileoverview File input component. Used for non-image files.
 */

import React, { ChangeEvent, Component } from "react";
import CloseIcon from "@jq-icons/Close";
import DownloadIcon from "@jq-icons/Download";
import InfoIcon from "@jq-icons/Info";
import JobPerFileIcon from "@jq-icons/JobPerFile";
import FlexItems from "../../../06-layout/03-flex";
import FlexItem from "../../../06-layout/03-flex/02-flex-item";
import Spacing from "../../../06-layout/07-spacing";
import Text from "../../../13-text/03-text";
import Button from "../../01-button";
import InlineError from "../../09-inline-error";
import FormLabel from "../../11-form-item/01-form-label";
import { FormDescription } from "../../11-form-item/02-form-description";
import styles from "./../styles.module.scss";

export interface FileInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The label to be shown next to the input.
   */
  label?: string | null;
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
  /**
   * The placeholder to be shown next to the input.
   */
  placeholder?: string;
  /**
   * The default view file label.
   */
  defaultViewFileLabel?: string;
  /**
   * The description is shown for multiple files.
   */
  description?: string;
  /**
   * The style for the button.
   *
   * @default "default"
   */
  buttonStyle?: "primary" | "secondary" | "default";
  /**
   * If the file upload allows multiple file selection.
   *
   * @default "false"
   */
  multiple?: boolean;
  /**
   * File types the input accepts. Separated by a comma.
   *
   * @param string accept
   */
  fileTypes?: string[];
  /**
   * Maximum file size of the selected files in MB.
   */
  maxSize?: number;
  /**
   * Max size label to display.
   *
   *  @default "File is too large."
   */
  maxSizeMessage?: string;
  /**
   * Show or hide the files after selection.
   *
   *  @default true
   */
  showSelection?: boolean;
  /**
   * Event when the file is chose.
   */
  onChange?(file: File | File[] | ChangeEvent): void;
  /**
   * Title for the remove button.
   */
  removeLabel?: string;
  /**
   * Add files callback.
   */
  onAdd?(file: File[]): void;
  /**
   * Remove file callback.
   */
  onRemove?(file: File): void;
  /**
   * Existing files. A list of URLs for existing files.
   *
   * @default []
   */
  defaultFiles?: string[];
  /**
   * Alt tag for the preview files.
   */
  previewExistingFilesAlt?: string;
  /**
   * The remove existing file callback.
   */
  onRemoveExistingFile?(index: number): void;
}

interface FileItem {
  /**
   * The file that was uploaded.
   */
  file: any;
  /**
   * Original file.
   */
  originalFile: File;
  /**
   * The name of the file that was uploaded.
   */
  filename: string;
  /**
   * The size of the file that was uploaded.
   */
  fileSize: number;
}

interface State {
  /**
   * The file that was uploaded.
   */
  files: FileItem[];
  /**
   * The errors to show the user.
   */
  errors: string[];
}

/**
 * A file input is a form element where users can chose a file from their local
 * computer and use it to upload.
 */
export class FileInput extends Component<FileInputProps, State> {
  /**
   * Internal property to hold the reference to the file input.
   */
  fileInput: React.RefObject<HTMLInputElement | null>;

  state = {
    files: [],
    errors: [],
  };

  constructor(props) {
    super(props);
    // create a ref to store the textInput DOM element
    this.fileInput = React.createRef<HTMLInputElement>();
  }

  /**
   * Triggers a click on the input.
   */
  handleClick = () => {
    if (this.fileInput.current) {
      this.fileInput.current.click();
    }
  };

  /**
   * Handle change on the input field.
   */
  handleChange = (event) => {
    const fileItems = Array.from(event.target.files) as File[];
    const { maxSizeMessage = "File is too large" } = this.props;

    this.props.onAdd?.(fileItems);

    this.setState(
      {
        errors: fileItems
          .filter((f) => {
            return (
              this.props.maxSize && f.size > this.props.maxSize * 1024 * 1024
            );
          })
          .map((fileItem) => {
            return `${fileItem.name} : ${maxSizeMessage}`;
          }),
        files: [
          ...(this.props.multiple ? this.state.files : []),
          ...fileItems
            .filter((f) => {
              return (
                !this.props.maxSize ||
                (this.props.maxSize &&
                  f.size <= this.props.maxSize * 1024 * 1024)
              );
            })
            .map((fileItem) => {
              return {
                file:
                  fileItem.type.substring(0, 5) === "image"
                    ? URL.createObjectURL(fileItem)
                    : null,
                originalFile: fileItem,
                name: fileItem.name,
                filename: fileItem.name,
                fileSize: fileItem.size,
              };
            }),
        ],
      },
      () => {
        if (this.props.multiple) {
          this.props.onChange && this.props.onChange(fileItems);
          if (this.props.showSelection === false) {
            if (this.fileInput.current) {
              this.fileInput.current.value = "";
            }
          }
        } else {
          this.props.onChange && this.props.onChange(fileItems[0]);
          if (!this.props.showSelection === false) {
            if (this.fileInput.current) {
              this.fileInput.current.value = "";
            }
          }
        }
      },
    );
  };

  /**
   * Handle change on the input field.
   */
  removeFile = (fileIndex) => {
    this.props.onRemove?.(this.state.files[fileIndex]);

    this.setState(
      {
        files: this.state.files
          .filter((_, index) => index !== fileIndex)
          .map((fileItem: FileItem) => {
            return {
              file: fileItem.file,
              originalFile: fileItem.originalFile,
              name: fileItem.filename,
              filename: fileItem.filename,
              fileSize: fileItem.fileSize,
            };
          }),
      },
      () => {
        if (this.props.multiple) {
          this.props.onChange &&
            this.props.onChange(
              this.state.files.map((f: FileItem) => f.originalFile),
            );
        } else {
          this.props.onChange &&
            this.props.onChange(
              this.state.files.map((f: FileItem) => f.originalFile),
            );
        }

        // Always reset if empty.
        if (!this.state.files || this.state.files.length === 0) {
          if (this.fileInput.current) {
            this.fileInput.current.value = "";
          }
        }
      },
    );
  };

  public render() {
    const {
      label = "File",
      placeholder = "Choose file",
      defaultViewFileLabel = "View file",
      id,
      description,
      fileTypes,
      error = false,
      required = false,
      disabled = false,
      multiple = false,
      buttonStyle = "default",
      onClick,
      showSelection = true,
      removeLabel = "Remove file",
      defaultFiles = [],
      onRemoveExistingFile,
    } = this.props;
    return (
      <>
        {label && multiple && (
          <>
            <FormLabel error={error} required={required} htmlFor={id}>
              {label}
            </FormLabel>
            <Spacing />
          </>
        )}
        <div className={`${styles.wrapper} ${multiple && styles.multiple}`}>
          {multiple && (
            <>
              <DownloadIcon
                aria-hidden="true"
                height="100px"
                width="100px"
                color="gray2"
              />
              <Spacing />
            </>
          )}
          <>
            {label && !multiple && (
              <FormLabel error={error} required={required} htmlFor={id}>
                {label}
              </FormLabel>
            )}
            {description && multiple && (
              <Text tag="p" color="gray3" textAlign="center">
                {description}
              </Text>
            )}
            <Spacing />
            <FlexItems>
              <div>
                <input
                  className={styles.fileWidgetInput}
                  ref={this.fileInput as React.RefObject<HTMLInputElement>}
                  onChange={this.handleChange}
                  type="file"
                  {...(fileTypes ? { accept: fileTypes.join(", ") } : null)}
                  id={id}
                  name={id}
                  onClick={onClick}
                  multiple={multiple}
                  disabled={disabled}
                  {...(!label && { "aria-labelledby": `${id}-btn` })}
                />
                <Button
                  onClick={this.handleClick}
                  data-testid="trigger"
                  buttonStyle={buttonStyle}
                  type="button"
                  id={`${id}-btn`}
                  disabled={disabled}
                >
                  {placeholder}
                </Button>
              </div>
            </FlexItems>
            {description && !multiple && (
              <FormDescription>{description}</FormDescription>
            )}
            {showSelection && (
              <FlexItems flexWrap="wrap" alignItems="center">
                {this.state.files.map((fileUploaded: FileItem, index) => (
                  <FlexItem margin={4} key={index}>
                    <div className={styles.boxFilePreviewWrapper}>
                      {!disabled && (
                        <div className={styles.removeFileButton}>
                          <button
                            type="button"
                            onClick={() => this.removeFile(index)}
                          >
                            <CloseIcon title={removeLabel} />
                          </button>
                        </div>
                      )}
                      <div className={styles.filePreviewWrapper}>
                        <div className={styles.imagePreviewWrapper}>
                          <JobPerFileIcon aria-hidden="true" />
                        </div>
                        <div className={styles.previewInfo}>
                          <div>
                            <span className={styles.imagePreviewInfoLabel}>
                              <span
                                className={styles.previewIcon}
                                {...(label && { title: label })}
                              />
                            </span>
                            <div>{fileUploaded.filename}</div>
                            {(fileUploaded.fileSize / 1000).toFixed(2)} kB
                          </div>
                        </div>
                      </div>
                    </div>
                  </FlexItem>
                ))}
              </FlexItems>
            )}
            {defaultFiles.length > 0 && (
              <FlexItems flexWrap="wrap" alignItems="center">
                {defaultFiles.map((defaultFile, index) => (
                  <FlexItem margin={4} key={index}>
                    <div className={styles.boxFilePreviewWrapper}>
                      {!disabled && (
                        <div className={styles.removeFileButton}>
                          <button
                            type="button"
                            onClick={() => onRemoveExistingFile?.(index)}
                          >
                            <CloseIcon title={removeLabel} />
                          </button>
                        </div>
                      )}
                      <div className={styles.filePreviewWrapper}>
                        <div className={styles.imagePreviewWrapper}>
                          <JobPerFileIcon aria-hidden="true" color="gray4" />

                          <div className={styles.previewInfo}>
                            <div>
                              <a
                                href={defaultFile}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {defaultFile.startsWith("http")
                                  ? // If the file is a URL, get the last part of the URL
                                    // as the file name otherwise use the default label.
                                    new URL(defaultFile)?.pathname
                                      .split("/")
                                      .pop()
                                  : defaultViewFileLabel}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FlexItem>
                ))}
              </FlexItems>
            )}
            {this.state.errors &&
              this.state.errors.map((errorMessage) => (
                <div>
                  <Spacing />
                  <FlexItems alignItems="center">
                    <FlexItem marginRight={4}>
                      <InfoIcon aria-hidden="true" color="danger" />{" "}
                    </FlexItem>
                    <InlineError message={errorMessage} />
                  </FlexItems>
                </div>
              ))}
          </>
        </div>
      </>
    );
  }
}

export default FileInput;
